// src/views/BackOffice/PropertyOwnershipReview.js
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardBody, CardTitle, Row, Col, Button, Alert, Spinner, Badge } from "reactstrap";
import axiosInstance from "../../api/Axios";

const PropertyOwnershipReview = () => {
  const navigate = useNavigate();
  const { propertyId } = useParams();
  
  // State management
  const [property, setProperty] = useState(null);
  const [ownershipZoning, setOwnershipZoning] = useState(null);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [lightboxDocId, setLightboxDocId] = useState(null);
  const [lightboxDocType, setLightboxDocType] = useState('image');
  const [lightboxFieldName, setLightboxFieldName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Backend base URL for media files
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

  // Helper function to determine file type
  const getFileType = (url) => {
    if (!url) return 'document';
    
    const extension = url.split('.').pop().toLowerCase();
    
    // PDF files
    if (['pdf'].includes(extension)) return 'pdf';
    
    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) return 'image';
    
    // Other document types
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'rtf'].includes(extension)) return 'document';
    
    // Default to document for unknown types
    return 'document';
  };

  // Fetch property and ownership/zoning data
  useEffect(() => {
    const fetchPropertyOwnershipData = async () => {
      if (!propertyId) {
        setError('Property ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch property and ownership details from back office endpoint
        const ownershipResponse = await axiosInstance.get(`/api/properties/${propertyId}/ownership-review/`);
        setProperty(ownershipResponse.data.property);
        setOwnershipZoning(ownershipResponse.data.ownership_zoning);
        
      } catch (err) {
        console.error('Error fetching property ownership data:', err);
        setError(err.response?.data?.error || 'Failed to fetch property ownership data');
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyOwnershipData();
  }, [propertyId]);

  // Handle document approval
  const handleAccept = async () => {
    if (!expiryDate) {
      alert("Please select an expiry date before accepting.");
      return;
    }
    
    try {
      // Debug: Log what we're sending
      console.log('DEBUG: Sending to update-status:', {
        property_id: propertyId,
        document_field: lightboxFieldName,
        status: "accepted"
      });
      
      // Update status to accepted
      await axiosInstance.post("/api/property-documents/update-status/", {
        property_id: propertyId,
        document_field: lightboxFieldName,
        status: "accepted"
      });
      
      // Update expiry date
      await axiosInstance.post("/api/property-documents/update-expiry/", {
        property_id: propertyId,
        document_field: lightboxFieldName,
        expiry_date: expiryDate
      });
      
      alert("Property ownership document accepted successfully!");
      
      // Close lightbox and reset form
      closeLightbox();
      
      // Refresh the ownership data
      const ownershipResponse = await axiosInstance.get(`/api/properties/${propertyId}/ownership-review/`);
      setProperty(ownershipResponse.data.property);
      setOwnershipZoning(ownershipResponse.data.ownership_zoning);
    } catch (error) {
      console.error("Error accepting property ownership document:", error);
      console.error("Error response:", error.response?.data);
      alert(`Error accepting document: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleReject = async () => {
    if (!comments.trim()) {
      alert("Please enter a comment before rejecting the document.");
      return;
    }
    
    try {
      // Update status to rejected
      await axiosInstance.post("/api/property-documents/update-status/", {
        property_id: propertyId,
        document_field: lightboxFieldName,
        status: "rejected"
      });
      
      // Update comments
      await axiosInstance.post("/api/property-documents/update-comments/", {
        property_id: propertyId,
        document_field: lightboxFieldName,
        comments: comments
      });
      
      alert("Property ownership document rejected successfully!");
      
      // Close lightbox and reset form
      closeLightbox();
      
      // Refresh the ownership data
      const ownershipResponse = await axiosInstance.get(`/api/properties/${propertyId}/ownership-review/`);
      setProperty(ownershipResponse.data.property);
      setOwnershipZoning(ownershipResponse.data.ownership_zoning);
    } catch (error) {
      console.error("Error rejecting property ownership document:", error);
      console.error("Error response:", error.response?.data);
      alert(`Error rejecting document: ${error.response?.data?.error || error.message}`);
    }
  };

  const closeLightbox = () => {
    setLightboxImg(null);
    setLightboxDocId(null);
    setLightboxDocType('image');
    setLightboxFieldName('');
    setExpiryDate('');
    setComments('');
  };

  const openDocument = (documentUrl, fieldName, existingExpiry = '', existingComments = '') => {
    if (!documentUrl) return;
    
    // Handle both relative and absolute URLs
    let fullUrl;
    if (documentUrl.startsWith('http://') || documentUrl.startsWith('https://')) {
      // URL is already absolute
      fullUrl = documentUrl;
    } else if (documentUrl.startsWith('/')) {
      // URL is relative from root
      fullUrl = `${BACKEND_URL}${documentUrl}`;
    } else {
      // URL is relative
      fullUrl = `${BACKEND_URL}/${documentUrl}`;
    }
    
    const fileType = getFileType(documentUrl);
    
    // Debug: Log the URL being used
    console.log('Opening document:', {
      originalUrl: documentUrl,
      fullUrl: fullUrl,
      fileType: fileType,
      fieldName: fieldName,
      existingExpiry,
      existingComments
    });
    
    setLightboxImg(fullUrl);
    setLightboxDocId(propertyId);
    setLightboxDocType(fileType);
    setLightboxFieldName(fieldName);
    
    // Pre-populate form with existing data
    setExpiryDate(existingExpiry || '');
    setComments(existingComments || '');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'not_provided': { color: 'secondary', text: 'Not Provided' },
      'pending_approval': { color: 'warning', text: 'Pending Approval' },
      'rejected': { color: 'danger', text: 'Rejected' },
      'accepted': { color: 'success', text: 'Accepted' }
    };
    
    const config = statusConfig[status] || statusConfig['not_provided'];
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const renderDocumentThumbnail = (documentUrl, fieldName, status, expiryDate = null, comments = null) => {
    if (!documentUrl) {
      return (
        <div className="text-center p-3 border rounded bg-light">
          <i className="bi bi-file-earmark-x text-muted" style={{ fontSize: '2rem' }}></i>
          <div className="small text-muted mt-1">No Document</div>
        </div>
      );
    }

    const fileType = getFileType(documentUrl);
    
    return (
      <div className="document-container text-center">
        <button
          onClick={() => openDocument(documentUrl, fieldName, expiryDate, comments)}
          style={{ 
            background: 'none', 
            border: 'none', 
            padding: 0,
            cursor: 'pointer',
            transition: 'transform 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          {fileType === 'pdf' ? (
            <div
              style={{
                width: 80,
                height: 80,
                backgroundColor: '#e74c3c',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
                border: "2px solid #ccc",
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
                margin: '0 auto'
              }}
            >
              PDF
            </div>
          ) : fileType === 'document' ? (
            <div
              style={{
                width: 80,
                height: 80,
                backgroundColor: '#3498db',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
                border: "2px solid #ccc",
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
                margin: '0 auto'
              }}
            >
              DOC
            </div>
          ) : (
            <img
              src={
                documentUrl.startsWith('http://') || documentUrl.startsWith('https://') 
                  ? documentUrl 
                  : documentUrl.startsWith('/') 
                    ? `${BACKEND_URL}${documentUrl}` 
                    : `${BACKEND_URL}/${documentUrl}`
              }
              alt="document"
              style={{ 
                width: 80, 
                height: 80, 
                objectFit: "cover", 
                borderRadius: 8, 
                border: "2px solid #ccc" 
              }}
              onError={(e) => {
                // Fallback to DOC icon if image fails to load
                e.target.style.display = 'none';
                const fallback = document.createElement('div');
                fallback.style.cssText = 'width: 80px; height: 80px; background-color: #3498db; display: flex; align-items: center; justify-content: center; border-radius: 8px; border: 2px solid #ccc; color: white; font-size: 12px; font-weight: bold; margin: 0 auto;';
                fallback.textContent = 'IMG';
                e.target.parentNode.appendChild(fallback);
              }}
            />
          )}
        </button>
        <div className="mt-2">
          {getStatusBadge(status)}
          {expiryDate && (
            <div className="small text-info mt-1">
              <i className="bi bi-calendar3"></i> Expires: {new Date(expiryDate).toLocaleDateString()}
            </div>
          )}
          {comments && (
            <div className="small text-warning mt-1" title={comments}>
              <i className="bi bi-chat-left-text"></i> Has comments
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center">
          <Spinner color="primary" />
          <p className="mt-2">Loading property ownership documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid py-4">
        <Alert color="danger">
          <h5>Error Loading Property Data</h5>
          <p>{error}</p>
          <Button 
            color="outline-danger" 
            size="sm"
            onClick={() => navigate('/backoffice-dashboard/ManagePropertyApplications')}
          >
            Back to Property Applications
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header Section */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">
                <i className="bi bi-file-earmark-text me-2 text-primary"></i>
                Property Ownership & Zoning Documents
              </h2>
              {property && (
                <p className="text-muted mb-1">
                  {property.trading_name} - Property ID: {property.id}
                </p>
              )}
            </div>
            <Button 
              color="outline-secondary" 
              onClick={() => navigate('/backoffice-dashboard/ManagePropertyApplications')}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Property Applications
            </Button>
          </div>
        </Col>
      </Row>

      {/* Property Information */}
      {property && (
        <Row className="mb-4">
          <Col>
            <Card>
              <CardBody>
                <CardTitle tag="h5">Property Information</CardTitle>
                <Row>
                  <Col md="6">
                    <strong>Trading Name:</strong> {property.trading_name || 'N/A'}<br/>
                    <strong>Address:</strong> {[property.addresslines, property.suburb, property.city, property.province].filter(Boolean).join(', ')}<br/>
                    <strong>Postcode:</strong> {property.postcode || 'N/A'}
                  </Col>
                  <Col md="6">
                    <strong>Rooms:</strong> {property.number_of_rooms || 0}<br/>
                    <strong>Beds:</strong> {property.number_of_beds || 0}<br/>
                    <strong>Status:</strong> <Badge color="secondary">{property.status}</Badge>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Documents Section */}
      {ownershipZoning && (
        <Row>
          <Col>
            <Card>
              <CardBody>
                <CardTitle tag="h5">Ownership & Zoning Documents</CardTitle>
                
                {/* Ownership Documents */}
                <Row className="mb-5">
                  <Col>
                    <h6 className="text-primary mb-3">
                      <i className="bi bi-house-door me-2"></i>
                      Ownership Documents
                    </h6>
                    <Row>
                      <Col md="4" className="mb-3">
                        <div className="text-center">
                          <h6 className="mb-2">Title Deed / Lease Agreement</h6>
                          <div className="small text-muted mb-2">
                            Type: {ownershipZoning.ownership_type === 'deed' ? 'Title Deed' : 'Lease Agreement'}
                            {ownershipZoning.ownership_type === 'lease' && ownershipZoning.lease_permission_page && (
                              <div>Page Reference: {ownershipZoning.lease_permission_page}</div>
                            )}
                          </div>
                          {renderDocumentThumbnail(
                            ownershipZoning.ownership,
                            'ownership_status',
                            ownershipZoning.ownership_status,
                            ownershipZoning.ownership_expiry_date,
                            ownershipZoning.ownership_comments
                          )}
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                {/* Land Use Documents */}
                <Row>
                  <Col>
                    <h6 className="text-success mb-3">
                      <i className="bi bi-geo-alt me-2"></i>
                      Land Use Documents
                    </h6>
                    <Row>
                      <Col md="4" className="mb-3">
                        <div className="text-center">
                          <h6 className="mb-2">Zoning Certificate</h6>
                          {renderDocumentThumbnail(
                            ownershipZoning.zoning_certificate,
                            'zoning_certificate_status',
                            ownershipZoning.zoning_certificate_status,
                            ownershipZoning.zoning_certificate_expiry_date,
                            ownershipZoning.zoning_certificate_comments
                          )}
                        </div>
                      </Col>
                      <Col md="4" className="mb-3">
                        <div className="text-center">
                          <h6 className="mb-2">Consent Use</h6>
                          {renderDocumentThumbnail(
                            ownershipZoning.consent_use,
                            'consent_use_status',
                            ownershipZoning.consent_use_status,
                            ownershipZoning.consent_use_expiry_date,
                            ownershipZoning.consent_use_comments
                          )}
                        </div>
                      </Col>
                      <Col md="4" className="mb-3">
                        <div className="text-center">
                          <h6 className="mb-2">Occupancy Certificate</h6>
                          {renderDocumentThumbnail(
                            ownershipZoning.occupancy_certificate,
                            'occupancy_certificate_status',
                            ownershipZoning.occupancy_certificate_status,
                            ownershipZoning.occupancy_certificate_expiry_date,
                            ownershipZoning.occupancy_certificate_comments
                          )}
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                {/* Legacy Zoning Document */}
                {ownershipZoning.zoning && (
                  <Row className="mt-4">
                    <Col>
                      <h6 className="text-warning mb-3">
                        <i className="bi bi-archive me-2"></i>
                        Legacy Documents
                      </h6>
                      <Row>
                        <Col md="4" className="mb-3">
                          <div className="text-center">
                            <h6 className="mb-2">Legacy Zoning Document</h6>
                            {renderDocumentThumbnail(
                              ownershipZoning.zoning,
                              'zoning_status',
                              ownershipZoning.zoning_status,
                              ownershipZoning.zoning_expiry_date,
                              ownershipZoning.zoning_comments
                            )}
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Document Lightbox */}
      {lightboxImg && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            flexDirection: 'column'
          }}
          onClick={closeLightbox}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '90%',
              maxHeight: '90%',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            {/* Document Display */}
            <div style={{ marginBottom: '20px', maxWidth: '800px', maxHeight: '600px' }}>
              {lightboxDocType === 'pdf' ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <div style={{ fontSize: '72px', color: '#dc3545', marginBottom: '20px' }}>
                    📄
                  </div>
                  <h4 style={{ color: '#333', marginBottom: '20px' }}>PDF Document</h4>
                  <p style={{ color: '#666', marginBottom: '30px' }}>
                    Click the button below to view the PDF document in a new tab.
                  </p>
                  <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <a 
                      href={lightboxImg} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: '#ffffff', 
                        textDecoration: 'none',
                        padding: '12px 24px',
                        backgroundColor: '#007bff',
                        borderRadius: '6px',
                        fontWeight: 'bold',
                        display: 'inline-block',
                        transition: 'background-color 0.3s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
                    >
                      📖 View PDF
                    </a>
                    <a 
                      href={lightboxImg} 
                      download
                      style={{ 
                        color: '#ffffff', 
                        textDecoration: 'none',
                        padding: '12px 24px',
                        backgroundColor: '#28a745',
                        borderRadius: '6px',
                        fontWeight: 'bold',
                        display: 'inline-block',
                        transition: 'background-color 0.3s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#1e7e34'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#28a745'}
                    >
                      💾 Download PDF
                    </a>
                  </div>
                </div>
              ) : lightboxDocType === 'document' ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <div style={{ fontSize: '48px', color: '#3498db', marginBottom: '20px' }}>
                    📄
                  </div>
                  <p>Document preview not available. Click the link below to download:</p>
                  <a 
                    href={lightboxImg} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#007bff', textDecoration: 'underline' }}
                  >
                    Download Document
                  </a>
                </div>
              ) : (
                <img 
                  src={lightboxImg} 
                  alt="Document" 
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              )}
            </div>

            {/* Action Controls */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '100%', maxWidth: '400px' }}>
              {/* Document Information */}
              <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <strong>Document:</strong> {lightboxFieldName?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </div>

              {/* Expiry Date Input */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Expiry Date (required for acceptance):
                </label>
                <input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    borderRadius: '4px', 
                    border: '1px solid #ccc' 
                  }}
                />
              </div>

              {/* Comments Input */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Comments (required for rejection):
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Enter comments for rejection..."
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    borderRadius: '4px', 
                    border: '1px solid #ccc',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  onClick={handleAccept}
                  style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Accept
                </button>
                <button
                  onClick={handleReject}
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Reject
                </button>
                <button
                  onClick={closeLightbox}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyOwnershipReview;